import { useCallback, useEffect, useState, type FormEvent } from "react";

import {
  createProject,
  createUnit,
  loadProjects,
  StoryClientError,
  type StoryProject,
} from "../api/client";
import { App } from "./App";
import { useStoryPreferences } from "../i18n";
import { sanitizeLocalReturnTo } from "../api/accounts";
import { SignInGate } from "../components/SignInGate";
export { accountsSignInUrl, sanitizeLocalReturnTo } from "../api/accounts";

const apiOrigin = import.meta.env.VITE_STORY_API_ORIGIN?.trim() || "http://127.0.0.1:8787";
const environment = import.meta.env.VITE_STORY_ENVIRONMENT === "preview" ? "preview" : "production";

type Gate = "loading" | "auth" | "error" | "project" | "studio";

function selectedProjectId(): string | null {
  const query = new URLSearchParams(window.location.search).get("project");
  return query || window.localStorage.getItem("xhalo-story-project");
}

function rememberProject(projectId: string) {
  window.localStorage.setItem("xhalo-story-project", projectId);
  const next = new URL(window.location.href);
  next.searchParams.set("project", projectId);
  window.history.replaceState({}, "", next);
}

export function StudioRoot() {
  const { t } = useStoryPreferences();
  const [gate, setGate] = useState<Gate>("loading");
  const [projectId, setProjectId] = useState("");
  const [projects, setProjects] = useState<StoryProject[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const isAuthCallback = window.location.pathname === "/auth/callback";
  const isLocalDesignPreview = import.meta.env.DEV && new URLSearchParams(window.location.search).get("demo") === "1";

  const bootstrap = useCallback(async () => {
    setGate("loading");
    setError(null);
    try {
      const items = await loadProjects({ apiOrigin, auth: {} });
      setProjects(items);
      const remembered = selectedProjectId();
      const selected = items.find((item) => item.id === remembered) ?? items[0];
      if (selected) {
        rememberProject(selected.id);
        setProjectId(selected.id);
        setGate("studio");
      } else {
        setGate("project");
      }
    } catch (cause) {
      if (cause instanceof StoryClientError && cause.code === "AUTH_REQUIRED") {
        setGate("auth");
        return;
      }
      setError(cause instanceof Error ? cause.message : "Story Studio 暂时不可用。");
      setGate("error");
    }
  }, []);

  useEffect(() => {
    if (isLocalDesignPreview) return;
    if (isAuthCallback) {
      const returnTo = sanitizeLocalReturnTo(new URLSearchParams(window.location.search).get("returnTo"));
      window.location.replace(returnTo);
      return;
    }
    void bootstrap();
  }, [bootstrap, isAuthCallback, isLocalDesignPreview]);

  async function handleProject(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      const created = await createProject({
        apiOrigin,
        name: String(form.get("name") ?? "").trim(),
        formatProfile: String(form.get("formatProfile") ?? "novel") as "novel" | "screenplay" | "short_drama" | "game_narrative" | "custom",
        constitution: {
          language: "zh-Hans",
          tone: String(form.get("tone") ?? "克制、清晰、尊重人物动机").trim(),
          humanAcceptanceMandatory: true,
        },
        auth: {},
      });
      await createUnit({
        apiOrigin,
        projectId: created.projectId,
        expectedHead: created.headCommitId,
        title: String(form.get("firstUnit") ?? "第一场").trim(),
        auth: {},
      });
      rememberProject(created.projectId);
      setProjectId(created.projectId);
      setGate("studio");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("temporaryUnavailable"));
    } finally {
      setBusy(false);
    }
  }

  if (isLocalDesignPreview) return <App />;
  if (gate === "studio") return <App config={{ apiOrigin, projectId, auth: {} }} />;
  if (gate === "auth") return <SignInGate />;

  return (
    <main className="entry-shell">
      <header className="entry-header">
        <div className="brand-orbit" aria-hidden="true"><span /></div>
        <div><strong>xHalo Story</strong><small>{environment === "preview" ? "Preview" : "Production"} · Canonical creation OS</small></div>
      </header>
      <section className="entry-card" aria-live="polite">
        {gate === "loading" ? <><p className="entry-kicker">正在连接 Canon Ledger</p><h1>准备你的创作空间</h1><div className="entry-progress"><span /></div></> : null}
        {gate === "error" ? <><h1>{t("unavailable")}</h1><button className="entry-primary" type="button" onClick={() => void bootstrap()}>{t("retry")}</button></> : null}
        {gate === "project" ? <>
          <p className="entry-kicker">{t("createProject")}</p><h1>{t("projectHeading")}</h1><p>{t("projectDescription")}</p>
          <form className="entry-form" onSubmit={handleProject}>
            <label>{t("projectName")}<input name="name" minLength={1} maxLength={120} placeholder="例如：雾港来信" required autoFocus /></label>
            <label>{t("format")}<select name="formatProfile" defaultValue="novel"><option value="novel">Novel</option><option value="screenplay">Screenplay</option><option value="short_drama">Short drama</option><option value="game_narrative">Game narrative</option><option value="custom">Custom</option></select></label>
            <label>{t("firstScene")}<input name="firstUnit" maxLength={200} defaultValue="场景 01 · 开端" required /></label>
            <label>{t("tone")}<textarea name="tone" maxLength={500} defaultValue="克制、清晰、尊重人物动机；任何 AI 变更必须经作者接受。" /></label>
            <button className="entry-primary" disabled={busy} type="submit">{busy ? t("creatingCanon") : t("createSpace")}<span>↗</span></button>
          </form>
        </> : null}
        {projects.length > 0 ? <p className="entry-security">已发现 {projects.length} 个可访问项目。</p> : null}
        {error ? <p className="entry-error" role="alert">{error}</p> : null}
      </section>
      <footer className="entry-footer"><span>Private by default</span><span>Draft ≠ Canon</span><span>Human acceptance required</span></footer>
    </main>
  );
}
